// src/components/home/StoriesBar.jsx
import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import StoryUploader from './StoryUploader';
import StoryViewer from './StoryViewer';
import StoryEditor from './StoryEditor';
import { formatTimeLeft, msUntilExpiry } from '../../utils/feedHelpers';

/* ================================================================== */
/* Helpers                                                             */
/* ================================================================== */

const toMillis = (value) => {
  if (value == null) return null;
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const t = Date.parse(value);
    return Number.isNaN(t) ? null : t;
  }
  if (value instanceof Date) return value.getTime();
  if (typeof value.toMillis === 'function') return value.toMillis();
  if (typeof value.toDate === 'function') {
    const d = value.toDate();
    return d instanceof Date && !Number.isNaN(d.getTime()) ? d.getTime() : null;
  }
  return null;
};

const getStoryCover = (story) => {
  if (!story) return null;
  if (Array.isArray(story.items) && story.items.length) {
    const first = story.items.find((it) => it && it.url);
    if (first) return { url: first.url, type: first.type || 'image' };
  }
  if (Array.isArray(story.photos) && story.photos.length) {
    const url = story.photos.find((u) => typeof u === 'string' && u);
    if (url) return { url, type: 'image' };
  }
  return null;
};

const unionViews = (all) => {
  const set = new Set();
  for (const s of all) {
    if (Array.isArray(s.views)) {
      for (const v of s.views) if (v) set.add(v);
    }
  }
  return [...set];
};

const sortByCreatedAsc = (list) =>
  [...list].sort((a, b) => {
    const at = toMillis(a?.createdAt) ?? 0;
    const bt = toMillis(b?.createdAt) ?? 0;
    return at - bt;
  });

/* ================================================================== */
/* Create Story Tile                                                   */
/* ================================================================== */

const CreateStoryTile = ({ onClick, disabled, userPhoto }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className="w-28 h-44 rounded-2xl flex-shrink-0 relative overflow-hidden group bg-gradient-to-br from-orange to-orange-dark hover:brightness-110 active:scale-[0.97] transition disabled:opacity-60 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-orange focus:ring-offset-2 dark:focus:ring-offset-gray-900 shadow-md snap-start"
    aria-label="Create a new story"
  >
    {userPhoto ? (
      <img
        src={userPhoto}
        alt=""
        className="absolute inset-0 w-full h-full object-cover opacity-60"
        loading="lazy"
      />
    ) : null}
    <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/80" />

    <div className="absolute inset-0 flex flex-col items-center justify-end pb-3 z-10">
      <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center shadow-md mb-2 group-hover:scale-110 group-active:scale-95 transition-transform border-2 border-orange">
        <i className="fas fa-plus text-orange text-lg" aria-hidden="true" />
      </div>
      <span className="text-white text-xs font-bold drop-shadow">
        Create Story
      </span>
    </div>
  </button>
);

/* ================================================================== */
/* Story Tile                                                          */
/* ================================================================== */

const StoryTile = ({ story, currentUserId, onOpen, onEdit, now }) => {
  const cover = getStoryCover(story);
  const isSelf = currentUserId === story.authorId;
  const viewed = Boolean(currentUserId && story.views?.includes(currentUserId));
  const isPending = story.approved === false && isSelf;

  const timeLeft = isSelf ? formatTimeLeft(story.expiresAt) : null;
  const msLeft = isSelf ? msUntilExpiry(story.expiresAt) : null;
  const isUrgent =
    typeof msLeft === 'number' && msLeft >= 0 && msLeft < 60 * 60 * 1000;

  const viewCount = story.views?.length || 0;
  const totalItems = story.count || story.all?.length || 1;
  const progressPct =
    totalItems > 0
      ? Math.min(100, Math.round((viewCount / totalItems) * 100))
      : 0;

  const latestForEdit = isSelf
    ? story.all.reduce((acc, cur) => {
        const at = toMillis(acc?.createdAt) ?? 0;
        const ct = toMillis(cur?.createdAt) ?? 0;
        return ct > at ? cur : acc;
      }, story.all[0])
    : null;

  const showEditButton = isSelf && latestForEdit;
  const showVideoIndicator = cover?.type === 'video' && !showEditButton;
  const videoIndicatorClass = showEditButton
    ? 'absolute top-2 right-10'
    : 'absolute top-2 right-2';

  return (
    <div className="w-28 h-44 rounded-2xl flex-shrink-0 relative overflow-hidden bg-gray-200 dark:bg-gray-700 group shadow-md snap-start">
      <button
        type="button"
        onClick={() => onOpen(story.authorId)}
        className="absolute inset-0 w-full h-full focus:outline-none focus:ring-2 focus:ring-orange focus:ring-inset rounded-2xl active:scale-[0.97] transition-transform"
        aria-label={`View ${story.authorName || 'user'}'s story${
          story.count > 1 ? ` (${story.count} stories)` : ''
        }${isPending ? ' — pending approval' : ''}`}
      >
        {cover ? (
          cover.type === 'video' ? (
            <video
              src={cover.url}
              className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              muted
              playsInline
              preload="metadata"
            />
          ) : (
            <img
              src={cover.url}
              alt=""
              loading="lazy"
              className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            />
          )
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-gray-300 to-gray-500 dark:from-gray-600 dark:to-gray-800" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-black/40" />
      </button>

      {showVideoIndicator && (
        <div
          className={`${videoIndicatorClass} w-6 h-6 rounded-full bg-black/60 text-white flex items-center justify-center pointer-events-none z-20 backdrop-blur-sm`}
        >
          <i className="fas fa-play text-[8px]" aria-hidden="true" />
        </div>
      )}

      {isPending && (
        <div className="absolute top-2 right-8 bg-yellow-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full z-20 shadow">
          Pending
        </div>
      )}

      {showEditButton && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onEdit(latestForEdit);
          }}
          aria-label="Edit story"
          title="Edit story"
          className="absolute top-2 right-2 w-6 h-6 rounded-full bg-black/60 text-white flex items-center justify-center opacity-100 sm:opacity-0 sm:group-hover:opacity-100 focus:opacity-100 transition z-30 hover:bg-black/80 active:scale-90 backdrop-blur-sm"
        >
          <i className="fas fa-pen text-[10px]" aria-hidden="true" />
        </button>
      )}

      <div className="absolute top-2 left-2 pointer-events-none z-10">
        <div className="relative">
          <div
            className={`w-10 h-10 rounded-full p-[3px] ${
              viewed ? 'bg-gray-400/60' : 'bg-orange'
            }`}
          >
            <div className="w-full h-full rounded-full bg-white dark:bg-gray-800 overflow-hidden">
              {story.authorPhoto ? (
                <img
                  src={story.authorPhoto}
                  alt=""
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-xs font-bold text-orange">
                  {story.authorName?.[0]?.toUpperCase() || 'U'}
                </div>
              )}
            </div>
          </div>
          {viewed && progressPct > 0 && progressPct < 100 && (
            <svg
              className="absolute inset-0 w-10 h-10 -rotate-90 pointer-events-none"
              viewBox="0 0 40 40"
              aria-hidden="true"
            >
              <circle
                cx="20"
                cy="20"
                r="18"
                fill="none"
                stroke="#10b981"
                strokeWidth="2"
                strokeDasharray={`${(progressPct / 100) * 113} 113`}
                strokeLinecap="round"
              />
            </svg>
          )}
        </div>
      </div>

      <div className="absolute bottom-2 left-2 right-2 text-left pointer-events-none z-10">
        <p className="text-white text-xs font-semibold truncate drop-shadow">
          {isSelf ? 'Your Story' : story.authorName || 'User'}
        </p>
        {story.count > 1 ? (
          <p className="text-white/70 text-[10px] mt-0.5">
            {story.count} stories
          </p>
        ) : (
          timeLeft &&
          !isPending && (
            <p
              className={`text-[10px] mt-0.5 ${
                isUrgent ? 'text-yellow-300' : 'text-white/70'
              }`}
            >
              <i className="fas fa-clock mr-0.5 text-[8px]" aria-hidden="true" />
              {timeLeft}
            </p>
          )
        )}
      </div>
    </div>
  );
};

/* ================================================================== */
/* Skeleton                                                            */
/* ================================================================== */

const StoriesSkeleton = () => (
  <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm p-3 mb-4 border border-gray-100 dark:border-gray-700/60">
    <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide">
      {[1, 2, 3, 4, 5].map((i) => (
        <div
          key={i}
          className="w-28 h-44 rounded-2xl bg-gray-200 dark:bg-gray-700 animate-pulse flex-shrink-0"
        />
      ))}
    </div>
  </div>
);

/* ================================================================== */
/* Empty State                                                         */
/* ================================================================== */

const StoriesEmptyState = ({ onCreate, isLoggedIn }) => (
  <div className="flex-1 flex items-center justify-center text-gray-500 dark:text-gray-400 py-8 px-4 min-w-[200px]">
    <div className="text-center">
      <div className="w-14 h-14 mx-auto mb-2 rounded-full bg-orange/10 flex items-center justify-center">
        <i className="fas fa-images text-orange text-xl" aria-hidden="true" />
      </div>
      <p className="text-sm font-semibold text-gray-700 dark:text-gray-200">
        No stories yet
      </p>
      <p className="text-[11px] mt-0.5">
        {isLoggedIn ? 'Be the first to share!' : 'Check back soon.'}
      </p>
      {isLoggedIn && (
        <button
          type="button"
          onClick={onCreate}
          className="mt-2 text-[11px] font-semibold text-orange hover:underline"
        >
          + Create Story
        </button>
      )}
    </div>
  </div>
);

/* ================================================================== */
/* StoriesBar                                                          */
/* ================================================================== */

const StoriesBar = ({ stories = [], loading = false }) => {
  const { user, userData } = useAuth();
  const [uploaderOpen, setUploaderOpen] = useState(false);
  const [viewerAuthorId, setViewerAuthorId] = useState(null);
  const [editorOpen, setEditorOpen] = useState(null);
  const [now, setNow] = useState(() => Date.now());

  const intervalRef = useRef(null);

  useEffect(() => {
    const start = () => {
      if (intervalRef.current) return;
      intervalRef.current = setInterval(() => setNow(Date.now()), 30 * 1000);
    };
    const stop = () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        setNow(Date.now());
        start();
      } else {
        stop();
      }
    };

    if (document.visibilityState === 'visible') start();
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  const grouped = useMemo(() => {
    const map = new Map();
    for (const s of stories) {
      if (!s || !s.authorId) continue;
      const expiresMs = toMillis(s.expiresAt);
      if (expiresMs != null && expiresMs <= now) continue;
      const existing = map.get(s.authorId);
      if (!existing) {
        map.set(s.authorId, {
          ...s,
          count: 1,
          all: [s],
          views: Array.isArray(s.views) ? [...s.views] : [],
        });
        continue;
      }
      const all = [...existing.all, s];
      const newest = all.reduce((acc, cur) => {
        const at = toMillis(acc?.createdAt) ?? 0;
        const ct = toMillis(cur?.createdAt) ?? 0;
        return ct > at ? cur : acc;
      }, all[0]);
      map.set(s.authorId, {
        ...existing,
        items: newest.items,
        photos: newest.photos,
        caption: newest.caption,
        createdAt: newest.createdAt,
        expiresAt: newest.expiresAt,
        approved: newest.approved,
        count: all.length,
        all: sortByCreatedAsc(all),
        views: unionViews(all),
      });
    }
    const list = [...map.values()];
    list.sort((a, b) => {
      if (user) {
        const aSelf = a.authorId === user.uid ? 1 : 0;
        const bSelf = b.authorId === user.uid ? 1 : 0;
        if (aSelf !== bSelf) return bSelf - aSelf;
      }
      const at = toMillis(a.createdAt) ?? 0;
      const bt = toMillis(b.createdAt) ?? 0;
      if (bt !== at) return bt - at;
      return String(a.authorId).localeCompare(String(b.authorId));
    });
    return list;
  }, [stories, user, now]);

  const flatStories = useMemo(() => grouped.flatMap((g) => g.all), [grouped]);

  useEffect(() => {
    if (viewerAuthorId == null) return;
    const stillPresent = grouped.some((s) => s.authorId === viewerAuthorId);
    if (!stillPresent) setViewerAuthorId(null);
  }, [grouped, viewerAuthorId]);

  const openUploader = useCallback(() => {
    setEditorOpen(null);
    setViewerAuthorId(null);
    setUploaderOpen(true);
  }, []);

  const handleOpenViewer = useCallback((authorId) => {
    setViewerAuthorId(authorId);
  }, []);

  const handleEdit = useCallback((story) => {
    setViewerAuthorId(null);
    setUploaderOpen(false);
    setEditorOpen(story);
  }, []);

  if (loading) {
    return <StoriesSkeleton />;
  }

  const showCreateTile = !!user;

  return (
    <>
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm p-3 mb-4 border border-gray-100 dark:border-gray-700/60">
        {/* Horizontal scroller */}
        <div
          className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide snap-x snap-mandatory"
          style={{ WebkitOverflowScrolling: 'touch' }}
        >
          {showCreateTile && (
            <CreateStoryTile
              onClick={openUploader}
              userPhoto={userData?.photoUrl}
            />
          )}

          {grouped.map((s) => (
            <StoryTile
              key={s.authorId}
              story={s}
              currentUserId={user?.uid}
              onOpen={handleOpenViewer}
              onEdit={handleEdit}
              now={now}
            />
          ))}

          {grouped.length === 0 && (
            <StoriesEmptyState
              onCreate={openUploader}
              isLoggedIn={!!user}
            />
          )}
        </div>
      </div>

      {uploaderOpen && (
        <StoryUploader
          onClose={() => setUploaderOpen(false)}
          authorName={userData?.displayName || 'User'}
          authorPhoto={userData?.photoUrl || null}
        />
      )}

      {viewerAuthorId != null && flatStories.length > 0 && (
        <StoryViewer
          key={`story-viewer-${flatStories.length}-${viewerAuthorId}`}
          stories={flatStories}
          initialIndex={flatStories.findIndex(
            (s) => s.authorId === viewerAuthorId,
          )}
          onClose={() => setViewerAuthorId(null)}
          onEdit={(story) => {
            setViewerAuthorId(null);
            setEditorOpen(story);
          }}
        />
      )}

      {editorOpen && (
        <StoryEditor
          story={editorOpen}
          onClose={() => setEditorOpen(null)}
          onUpdated={() => {}}
          onDeleted={() => {}}
        />
      )}
    </>
  );
};

export default StoriesBar;